import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from './config';
import { Schedule, ScheduleFormData, WorkingHours } from '../../types/schedule.types';

/**
 * Clean undefined values from an object (Firestore doesn't support undefined)
 * Recursively removes undefined values from nested objects
 */
function cleanUndefinedValues<T extends Record<string, unknown>>(obj: T): Partial<T> {
  const cleaned: Record<string, unknown> = {};

  for (const key in obj) {
    const value = obj[key];

    if (value === undefined) {
      // Skip undefined values
      continue;
    } else if (Array.isArray(value)) {
      // Clean arrays recursively
      cleaned[key] = value.map((item) =>
        typeof item === 'object' && item !== null
          ? cleanUndefinedValues(item as Record<string, unknown>)
          : item
      );
    } else if (value !== null && typeof value === 'object') {
      // Recursively clean nested objects
      cleaned[key] = cleanUndefinedValues(value as Record<string, unknown>);
    } else {
      // Keep defined values as-is
      cleaned[key] = value;
    }
  }

  return cleaned as Partial<T>;
}

/**
 * Firebase Schedules Service
 * Handles schedule CRUD operations for organizations
 */
export const schedulesService = {
  /**
   * Create a new schedule for a shop on a specific date
   */
  async createSchedule(organizationId: string, data: ScheduleFormData): Promise<string> {
    try {
      const scheduleRef = doc(collection(db, 'organizations', organizationId, 'schedules'));
      const scheduleId = scheduleRef.id;

      // Extract month from date (YYYY-MM)
      const month = data.date.substring(0, 7);

      // Clean the data first (before adding timestamps)
      const dataToClean = {
        shopId: data.shopId,
        date: data.date,
        month,
        employeeSchedules: data.employeeSchedules,
        availabilityMode: data.availabilityMode,
      };

      const cleanedData = cleanUndefinedValues(dataToClean as unknown as Record<string, unknown>);

      // Add timestamps AFTER cleaning to avoid corrupting sentinel values
      const scheduleData = {
        ...cleanedData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      await setDoc(scheduleRef, scheduleData);

      return scheduleId;
    } catch (error) {
      console.error('Create schedule error:', error);
      throw error;
    }
  },

  /**
   * Get a schedule by ID
   */
  async getSchedule(organizationId: string, scheduleId: string): Promise<Schedule | null> {
    try {
      const scheduleDoc = await getDoc(
        doc(db, 'organizations', organizationId, 'schedules', scheduleId)
      );

      if (scheduleDoc.exists()) {
        return {
          id: scheduleDoc.id,
          ...scheduleDoc.data(),
        } as Schedule;
      }

      return null;
    } catch (error) {
      console.error('Get schedule error:', error);
      throw error;
    }
  },

  /**
   * Get schedule for a specific shop and date
   */
  async getScheduleByShopAndDate(
    organizationId: string,
    shopId: string,
    date: string
  ): Promise<Schedule | null> {
    try {
      const schedulesRef = collection(db, 'organizations', organizationId, 'schedules');
      const q = query(
        schedulesRef,
        where('shopId', '==', shopId),
        where('date', '==', date)
      );
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        return {
          id: doc.id,
          ...doc.data(),
        } as Schedule;
      }

      return null;
    } catch (error) {
      console.error('Get schedule by shop and date error:', error);
      throw error;
    }
  },

  /**
   * Get all schedules for a shop in a specific month
   */
  async getSchedulesByShopAndMonth(
    organizationId: string,
    shopId: string,
    month: string // Format: "YYYY-MM"
  ): Promise<Schedule[]> {
    try {
      const schedulesRef = collection(db, 'organizations', organizationId, 'schedules');
      const q = query(
        schedulesRef,
        where('shopId', '==', shopId),
        where('month', '==', month),
        orderBy('date', 'asc')
      );
      const querySnapshot = await getDocs(q);

      const schedules: Schedule[] = [];
      querySnapshot.forEach((doc) => {
        schedules.push({
          id: doc.id,
          ...doc.data(),
        } as Schedule);
      });

      return schedules;
    } catch (error) {
      console.error('Get schedules by shop and month error:', error);
      throw error;
    }
  },

  /**
   * Get all schedules for a shop in a date range
   */
  async getSchedulesByShopAndDateRange(
    organizationId: string,
    shopId: string,
    startDate: string,
    endDate: string
  ): Promise<Schedule[]> {
    try {
      const schedulesRef = collection(db, 'organizations', organizationId, 'schedules');
      const q = query(
        schedulesRef,
        where('shopId', '==', shopId),
        where('date', '>=', startDate),
        where('date', '<=', endDate),
        orderBy('date', 'asc')
      );
      const querySnapshot = await getDocs(q);

      const schedules: Schedule[] = [];
      querySnapshot.forEach((doc) => {
        schedules.push({
          id: doc.id,
          ...doc.data(),
        } as Schedule);
      });

      return schedules;
    } catch (error) {
      console.error('Get schedules by shop and date range error:', error);
      throw error;
    }
  },

  /**
   * Get all schedules for all shops in a specific month
   */
  async getSchedulesByMonth(
    organizationId: string,
    month: string // Format: "YYYY-MM"
  ): Promise<Schedule[]> {
    try {
      const schedulesRef = collection(db, 'organizations', organizationId, 'schedules');
      const q = query(
        schedulesRef,
        where('month', '==', month),
        orderBy('date', 'asc')
      );
      const querySnapshot = await getDocs(q);

      const schedules: Schedule[] = [];
      querySnapshot.forEach((doc) => {
        schedules.push({
          id: doc.id,
          ...doc.data(),
        } as Schedule);
      });

      return schedules;
    } catch (error) {
      console.error('Get schedules by month error:', error);
      throw error;
    }
  },

  /**
   * Get all schedules for all shops in a date range
   */
  async getSchedulesByDateRange(
    organizationId: string,
    startDate: string, // Format: "YYYY-MM-DD"
    endDate: string // Format: "YYYY-MM-DD"
  ): Promise<Schedule[]> {
    try {
      const schedulesRef = collection(db, 'organizations', organizationId, 'schedules');
      const q = query(
        schedulesRef,
        where('date', '>=', startDate),
        where('date', '<=', endDate),
        orderBy('date', 'asc')
      );
      const querySnapshot = await getDocs(q);

      const schedules: Schedule[] = [];
      querySnapshot.forEach((doc) => {
        schedules.push({
          id: doc.id,
          ...doc.data(),
        } as Schedule);
      });

      return schedules;
    } catch (error) {
      console.error('Get schedules by date range error:', error);
      throw error;
    }
  },

  /**
   * Update a schedule
   */
  async updateSchedule(
    organizationId: string,
    scheduleId: string,
    data: Partial<ScheduleFormData>
  ): Promise<void> {
    try {
      const scheduleRef = doc(db, 'organizations', organizationId, 'schedules', scheduleId);

      const updateData: Record<string, unknown> = {};

      if (data.employeeSchedules !== undefined) {
        updateData.employeeSchedules = data.employeeSchedules;
      }

      if (data.shopId) {
        updateData.shopId = data.shopId;
      }

      if (data.date) {
        updateData.date = data.date;
        updateData.month = data.date.substring(0, 7);
      }

      if (data.availabilityMode !== undefined) {
        updateData.availabilityMode = data.availabilityMode;
      }

      // Clean undefined values (Firestore doesn't support undefined)
      const cleanedData = cleanUndefinedValues(updateData);

      // Add timestamp AFTER cleaning to avoid corrupting sentinel value
      const finalData = {
        ...cleanedData,
        updatedAt: serverTimestamp(),
      };

      await updateDoc(scheduleRef, finalData);
    } catch (error) {
      console.error('Update schedule error:', error);
      throw error;
    }
  },

  /**
   * Delete a schedule
   */
  async deleteSchedule(organizationId: string, scheduleId: string): Promise<void> {
    try {
      const scheduleRef = doc(db, 'organizations', organizationId, 'schedules', scheduleId);
      await deleteDoc(scheduleRef);
    } catch (error) {
      console.error('Delete schedule error:', error);
      throw error;
    }
  },

  /**
   * Add or update a working hours entry for an employee on a specific date
   */
  async addOrUpdateEmployeeWorkingHours(
    organizationId: string,
    scheduleId: string,
    employeeId: string,
    employeeName: string,
    workingHours: WorkingHours
  ): Promise<void> {
    try {
      const scheduleDoc = await this.getSchedule(organizationId, scheduleId);
      if (!scheduleDoc) {
        throw new Error('Schedule not found');
      }

      // Ensure employeeSchedules is an array
      let updatedEmployeeSchedules = Array.isArray(scheduleDoc.employeeSchedules)
        ? [...scheduleDoc.employeeSchedules]
        : [];
      const existingIndex = updatedEmployeeSchedules.findIndex((es) => es.employeeId === employeeId);

      if (existingIndex !== -1) {
        // Employee exists, add or update working hours
        const employeeSchedule = updatedEmployeeSchedules[existingIndex];
        const workingHoursArray = Array.isArray(employeeSchedule.workingHours)
          ? [...employeeSchedule.workingHours]
          : [];

        // Check if we're updating existing entry or adding new
        const existingHoursIndex = workingHoursArray.findIndex((wh) => wh.id === workingHours.id);
        if (existingHoursIndex !== -1) {
          // Update existing entry
          workingHoursArray[existingHoursIndex] = workingHours;
        } else {
          // Add new entry
          workingHoursArray.push(workingHours);
        }

        updatedEmployeeSchedules[existingIndex] = {
          employeeId,
          employeeName,
          workingHours: workingHoursArray,
        };
      } else {
        // Employee doesn't exist, create new entry
        updatedEmployeeSchedules.push({
          employeeId,
          employeeName,
          workingHours: [workingHours],
        });
      }

      await this.updateSchedule(organizationId, scheduleId, {
        employeeSchedules: updatedEmployeeSchedules,
      });
    } catch (error) {
      console.error('Add/update employee working hours error:', error);
      throw error;
    }
  },

  /**
   * Remove a specific working hours entry for an employee
   */
  async removeEmployeeWorkingHours(
    organizationId: string,
    scheduleId: string,
    employeeId: string,
    workingHoursId: string
  ): Promise<void> {
    try {
      const scheduleDoc = await this.getSchedule(organizationId, scheduleId);
      if (!scheduleDoc) {
        throw new Error('Schedule not found');
      }

      // Ensure employeeSchedules is an array
      let updatedEmployeeSchedules = Array.isArray(scheduleDoc.employeeSchedules)
        ? [...scheduleDoc.employeeSchedules]
        : [];
      const existingIndex = updatedEmployeeSchedules.findIndex((es) => es.employeeId === employeeId);

      if (existingIndex !== -1) {
        const employeeSchedule = updatedEmployeeSchedules[existingIndex];
        const workingHoursArray = Array.isArray(employeeSchedule.workingHours)
          ? [...employeeSchedule.workingHours]
          : [];

        // Remove the specific working hours entry
        const filteredHours = workingHoursArray.filter((wh) => wh.id !== workingHoursId);

        if (filteredHours.length === 0) {
          // No more working hours, remove the entire employee entry
          updatedEmployeeSchedules.splice(existingIndex, 1);
        } else {
          // Update with remaining working hours
          updatedEmployeeSchedules[existingIndex] = {
            ...employeeSchedule,
            workingHours: filteredHours,
          };
        }

        await this.updateSchedule(organizationId, scheduleId, {
          employeeSchedules: updatedEmployeeSchedules,
        });
      }
    } catch (error) {
      console.error('Remove employee working hours error:', error);
      throw error;
    }
  },

  /**
   * Bulk update employee working hours for a schedule
   * Reads the schedule once, applies all changes in memory, writes once
   * Much more efficient than individual addOrUpdate/remove calls
   */
  async bulkUpdateEmployeeWorkingHours(
    organizationId: string,
    scheduleId: string,
    changes: Array<{
      employeeId: string;
      employeeName: string;
      workingHours: WorkingHours[];
    }>
  ): Promise<void> {
    try {
      const scheduleDoc = await this.getSchedule(organizationId, scheduleId);
      if (!scheduleDoc) {
        throw new Error('Schedule not found');
      }

      // Start with existing employee schedules
      let updatedEmployeeSchedules = Array.isArray(scheduleDoc.employeeSchedules)
        ? [...scheduleDoc.employeeSchedules]
        : [];

      // Apply each employee's changes
      for (const change of changes) {
        const existingIndex = updatedEmployeeSchedules.findIndex(
          (es) => es.employeeId === change.employeeId
        );

        if (change.workingHours.length === 0) {
          // No working hours - remove employee from schedule if exists
          if (existingIndex !== -1) {
            updatedEmployeeSchedules.splice(existingIndex, 1);
          }
        } else {
          // Has working hours - add or update
          if (existingIndex !== -1) {
            // Update existing employee entry
            updatedEmployeeSchedules[existingIndex] = {
              employeeId: change.employeeId,
              employeeName: change.employeeName,
              workingHours: change.workingHours,
            };
          } else {
            // Add new employee entry
            updatedEmployeeSchedules.push({
              employeeId: change.employeeId,
              employeeName: change.employeeName,
              workingHours: change.workingHours,
            });
          }
        }
      }

      // Single write operation
      await this.updateSchedule(organizationId, scheduleId, {
        employeeSchedules: updatedEmployeeSchedules,
      });
    } catch (error) {
      console.error('Bulk update employee working hours error:', error);
      throw error;
    }
  },
};
